// pages/DashboardPage.jsx - Complete with Staff Roles Tab & Family Management & Membership Categories & Parent Approvals & Feedback System
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardService from '../services/dashboardService';
import { raffleService } from '../services/raffleService';
import { bookingService } from '../services/bookingService';
import { eventService } from '../services/eventService';
import { sectionService } from '../services/sectionService';
import StaffAssignment from '../components/StaffAssignment';
import FeedbackModal from '../components/FeedbackModal';
import AdminFeedbackPanel from '../components/AdminFeedbackPanel';
import { feedbackService } from '../services/feedbackService';
import './DashboardPage.css';

/* ─── Animation Variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  exit:   { opacity: 0, y: -16, transition: { duration: 0.25 } }
};

const stagger = {
  show: { transition: { staggerChildren: 0.07 } }
};

const slideIn = {
  hidden: { opacity: 0, x: -20 },
  show:   { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } }
};

/* ─── Nav Items ─── */
const NAV_ITEMS = [
  { id: 'overview',    label: 'Overview',    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
  { id: 'profile',     label: 'Profile',     icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  { id: 'parent-approvals', label: 'Family Approvals', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', primaryOnly: true },
  { id: 'family',      label: 'Family',      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', primaryOnly: true },
  { id: 'bookings',    label: 'Bookings',    icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
  { id: 'raffle',      label: 'Raffle',      icon: 'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z' },
  { id: 'events',      label: 'Events',      icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
  { id: 'sections',    label: 'My Sections', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { id: 'staff-roles', label: 'Staff Roles', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
  { id: 'categories',  label: 'Categories',  icon: 'M7 20l4-16m2 16l4-16M6 9h14M4 15h14', adminOnly: true },
  { id: 'activities',  label: 'Activities',  icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
  { id: 'my-feedback', label: 'My Feedback 📋', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z' },
  { id: 'feedback',    label: 'Feedback Admin', icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z', adminOnly: true },
];

/* ─────────────────────────────────────────────────── */
/*                  MAIN COMPONENT                     */
/* ─────────────────────────────────────────────────── */
const DashboardPage = () => {
  const navigate = useNavigate();

  const [user, setUser]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState('overview');
  const [toast, setToast]           = useState(null);
  const [sidebarOpen, setSidebar]   = useState(false);

  // ========== FEEDBACK STATE ==========
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [myFeedbacks, setMyFeedbacks] = useState([]);
  const [myFeedbacksLoading, setMyFeedbacksLoading] = useState(false);

  const [activeRaffle, setActiveRaffle]       = useState(null);
  const [myRaffleTickets, setMyRaffleTickets] = useState([]);
  const [raffleWinnings, setRaffleWinnings]   = useState([]);

  const [recentBookings, setRecentBookings]   = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);

  // Events state
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  
  // Pagination states for events tab
  const [currentEventPage, setCurrentEventPage] = useState(1);
  const [eventsPerPage] = useState(4);

  // Sections state
  const [allSections, setAllSections] = useState([]);
  const [mySections, setMySections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionFormData, setSectionFormData] = useState({
    name: '',
    category: '',
    description: '',
    instructor: '',
    schedule: '',
    duration: '',
    location: '',
    capacity: '',
    level: '',
    age_group: '',
    image: ''
  });

  // ========== MEMBERSHIP CATEGORY STATE ==========
  const [membershipCategories, setMembershipCategories] = useState([]);
  const [categoryStats, setCategoryStats] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [membersByCategory, setMembersByCategory] = useState([]);
  const [showCategoryMembers, setShowCategoryMembers] = useState(false);
  const [selectedMemberForCategory, setSelectedMemberForCategory] = useState(null);
  const [updatingCategory, setUpdatingCategory] = useState(false);

  // ========== PARENT APPROVALS STATE ==========
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [processingApprovalId, setProcessingApprovalId] = useState(null);

  const [dashboardData, setDashboardData] = useState({
    member_info: {}, family_members: [],
    upcoming_events: [], recent_activities: [],
    stats: { total_members:0, active_members:0, family_members:0, upcoming_events:0 }
  });

  const [familyForm, setFamilyForm] = useState({
    primary_member_id:'', full_name:'', email:'', phone:'',
    relationship:'spouse', date_of_birth:'', address:''
  });
  const [profileForm, setProfileForm] = useState({
    member_id:'', first_name:'', last_name:'', email:'', phone:'',
    current_password:'', new_password:'', confirm_password:''
  });
  const [editFamilyForm, setEditFamilyForm] = useState({
    family_member_id:'', full_name:'', phone:'', relationship:'spouse'
  });

  const [showAddFamily, setShowAddFamily]     = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showEditFamily, setShowEditFamily]   = useState(false);

  const showToast = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  // ========== HELPER FUNCTIONS FOR MEMBERSHIP DISPLAY ==========
  
  const getCombinedMembershipDisplay = () => {
    if (!user) return '';
    const typeLabel = user.membership_type === 'primary' ? 'Primary' : 'Dependent';
    let categoryLabel = '';
    if (user.membership_category) {
      const category = DashboardService.getMembershipCategory(user.membership_category);
      categoryLabel = category ? category.label : user.membership_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    if (categoryLabel) return `${typeLabel} - ${categoryLabel}`;
    return typeLabel === 'Primary' ? 'Primary Member' : 'Dependent Member';
  };

  const getMembershipTypeLabel = () => {
    if (!user) return '';
    return user.membership_type === 'primary' ? 'Primary Member' : 'Dependent Member';
  };

  const getCategoryLabelOnly = () => {
    if (!user?.membership_category) return '';
    const category = DashboardService.getMembershipCategory(user.membership_category);
    return category ? category.label : user.membership_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCategoryPrefixOnly = () => {
    if (!user?.membership_category) return '';
    const category = DashboardService.getMembershipCategory(user.membership_category);
    return category ? category.prefix : '';
  };

  // ========== PARENT APPROVALS FUNCTIONS ==========
  const fetchPendingApprovals = useCallback(async () => {
    setApprovalsLoading(true);
    try {
      const token = localStorage.getItem('nlngra_jwt');
      const response = await fetch('https://nlngraclub.com/nlnapi/get_pending_approvals.php', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setPendingApprovals(data.pending_approvals || []);
        setApprovalHistory(data.history || []);
      } else {
        showToast(data.message || 'Failed to load pending approvals', 'error');
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      showToast('Network error. Please try again.', 'error');
    } finally {
      setApprovalsLoading(false);
    }
  }, [showToast]);

  const handleApprove = async (dependentId, token) => {
    setProcessingApprovalId(dependentId);
    try {
      const jwtToken = localStorage.getItem('nlngra_jwt');
      const response = await fetch('https://nlngraclub.com/nlnapi/parent_approve.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({
          token: token,
          action: 'approve',
          dependent_id: dependentId
        })
      });

      const data = await response.json();

      if (data.success) {
        showToast(`${data.dependent_name || 'Dependent'} has been approved successfully!`, 'success');
        await fetchPendingApprovals();
      } else {
        showToast(data.message || 'Failed to approve dependent', 'error');
      }
    } catch (error) {
      console.error('Error approving:', error);
      showToast('Failed to process approval. Please try again.', 'error');
    } finally {
      setProcessingApprovalId(null);
    }
  };

  const handleDecline = async (dependentId, token) => {
    setProcessingApprovalId(dependentId);
    try {
      const jwtToken = localStorage.getItem('nlngra_jwt');
      const response = await fetch('https://nlngraclub.com/nlnapi/parent_approve.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({
          token: token,
          action: 'decline',
          dependent_id: dependentId
        })
      });

      const data = await response.json();

      if (data.success) {
        showToast(`${data.dependent_name || 'Dependent'} has been declined.`, 'info');
        await fetchPendingApprovals();
      } else {
        showToast(data.message || 'Failed to decline dependent', 'error');
      }
    } catch (error) {
      console.error('Error declining:', error);
      showToast('Failed to process decline. Please try again.', 'error');
    } finally {
      setProcessingApprovalId(null);
    }
  };

  // ========== MEMBERSHIP CATEGORY FUNCTIONS ==========
  const loadMembershipCategories = async () => {
    try {
      const response = await DashboardService.getMembershipCategories();
      if (response.success) {
        setMembershipCategories(response.categories);
      } else {
        setMembershipCategories(DashboardService.getMembershipCategoriesList());
      }
    } catch (error) {
      console.error('Error loading membership categories:', error);
      setMembershipCategories(DashboardService.getMembershipCategoriesList());
    }
  };

  const loadCategoryStats = async () => {
    try {
      const response = await DashboardService.getCategoryStats();
      if (response.success) {
        setCategoryStats(response.stats);
      }
    } catch (error) {
      console.error('Error loading category stats:', error);
    }
  };

  const loadMembersByCategory = async (category) => {
    try {
      setSelectedCategory(category);
      const response = await DashboardService.getMembersByCategory(category);
      if (response.success) {
        setMembersByCategory(response.members);
        setShowCategoryMembers(true);
      } else {
        showToast(response.error || 'Failed to load members', 'error');
      }
    } catch (error) {
      console.error('Error loading members by category:', error);
      showToast('Failed to load members for this category', 'error');
    }
  };

  const handleUpdateMemberCategory = async (memberId, newCategory) => {
    setUpdatingCategory(true);
    try {
      const response = await DashboardService.updateMemberCategory(memberId, newCategory);
      if (response.success) {
        showToast(`Member category updated successfully!`, 'success');
        await loadCategoryStats();
        if (selectedCategory) {
          await loadMembersByCategory(selectedCategory);
        }
        if (user && user.id === memberId) {
          const updatedUser = { ...user, membership_category: newCategory };
          setUser(updatedUser);
          localStorage.setItem('nlngra_user', JSON.stringify(updatedUser));
        }
        setSelectedMemberForCategory(null);
      } else {
        showToast(response.error || 'Failed to update member category', 'error');
      }
    } catch (error) {
      console.error('Error updating member category:', error);
      showToast('Failed to update member category', 'error');
    } finally {
      setUpdatingCategory(false);
    }
  };

  const getUserCategoryDisplay = () => {
    if (!user?.membership_category) return null;
    const prefix = getCategoryPrefixOnly();
    const label = getCategoryLabelOnly();
    if (!label) return null;
    return (
      <div className="db-category-badge">
        <span className="db-category-prefix">{prefix}</span>
        <span className="db-category-label">{label}</span>
      </div>
    );
  };

  // ========== LOAD MY FEEDBACKS ==========
  const loadMyFeedbacks = async () => {
    console.log('📝 [DEBUG] loadMyFeedbacks called');
    console.log('📝 [DEBUG] user object:', user);
    
    if (!user) {
      console.log('📝 [DEBUG] No user, skipping loadMyFeedbacks');
      return;
    }
    
    console.log('📝 [DEBUG] Loading feedbacks for user ID:', user.id);
    setMyFeedbacksLoading(true);
    
    try {
      const result = await feedbackService.getMyFeedbacks();
      console.log('📝 [DEBUG] Feedback API response:', result);
      
      if (result.success) {
        console.log('📝 [DEBUG] Found', result.feedbacks?.length || 0, 'feedbacks');
        setMyFeedbacks(result.feedbacks || []);
      } else {
        console.log('📝 [DEBUG] Failed to load feedbacks:', result.message);
        setMyFeedbacks([]);
      }
    } catch (error) {
      console.error('📝 [DEBUG] Error loading my feedbacks:', error);
      setMyFeedbacks([]);
    } finally {
      setMyFeedbacksLoading(false);
    }
  };

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const response = await eventService.getEvents(true, 100);
      if (response.success) {
        setUpcomingEvents(response.events);
        setCurrentEventPage(1);
      }
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setEventsLoading(false);
    }
  };

  const loadSections = async () => {
    setSectionsLoading(true);
    try {
      const allSectionsResponse = await sectionService.getAllSectionsPublic();
      if (allSectionsResponse.success) {
        setAllSections(allSectionsResponse.sections || []);
        console.log('✅ All sections loaded:', allSectionsResponse.sections?.length);
      }
      
      const mySectionsResponse = await sectionService.getMyMemberships();
      if (mySectionsResponse.success) {
        const enrichedMemberships = (mySectionsResponse.memberships || []).map(membership => {
          const sectionDetail = (allSectionsResponse.sections || []).find(s => s.id === membership.section_id);
          return {
            ...membership,
            section_name: sectionDetail?.name || membership.section_name || `Section #${membership.section_id}`,
            section_category: sectionDetail?.category || membership.category || '',
            section_description: sectionDetail?.description || membership.description || '',
            section_instructor: sectionDetail?.instructor || membership.instructor || '',
            section_schedule: sectionDetail?.schedule || membership.schedule || '',
            section_location: sectionDetail?.location || membership.location || '',
            section_image: sectionDetail?.image || membership.image || '',
            section_capacity: sectionDetail?.capacity || membership.capacity || '',
            section_level: sectionDetail?.level || membership.level || '',
            section_age_group: sectionDetail?.age_group || membership.age_group || ''
          };
        });
        setMySections(enrichedMemberships);
        console.log('✅ My sections loaded:', enrichedMemberships.length);
      }
    } catch (error) {
      console.error('Error loading sections:', error);
    } finally {
      setSectionsLoading(false);
    }
  };

  const handleUpdateSection = async (e) => {
    e.preventDefault();
    setSectionsLoading(true);
    try {
      showToast('Section updated successfully!', 'success');
      setShowSectionModal(false);
      setEditingSection(null);
      loadSections();
    } catch (error) {
      console.error('Error updating section:', error);
      showToast('Failed to update section', 'error');
    } finally {
      setSectionsLoading(false);
    }
  };

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const loadFamilyRelationship = async () => {
    try {
      const response = await DashboardService.getMyInfo();
      if (response.success && response.data) {
        const userData = {
          ...user,
          ...response.data.user,
          relationship_info: response.data.relationship_info
        };
        setUser(userData);
        localStorage.setItem('nlngra_user', JSON.stringify(userData));
        return userData;
      }
    } catch (error) {
      console.error('Error loading family relationship:', error);
    }
    return user;
  };

  /* ── Load ── */
  useEffect(() => {
    (async () => {
      try {
        const userStr = localStorage.getItem('nlngra_user');
        const token   = localStorage.getItem('nlngra_token');
        if (!userStr || !token) { navigate('/login'); return; }
        
        let userData = JSON.parse(userStr);
        
        if (userData.membership_type === 'dependent') {
          try {
            const familyInfo = await DashboardService.getMyInfo();
            if (familyInfo.success && familyInfo.data) {
              userData = {
                ...userData,
                ...familyInfo.data.user,
                relationship_info: familyInfo.data.relationship_info
              };
              localStorage.setItem('nlngra_user', JSON.stringify(userData));
            }
          } catch (err) {
            console.log('Could not get family info:', err);
          }
        }
        
        setUser(userData);
        setProfileForm(p => ({
          ...p, member_id: userData.id,
          first_name: userData.first_name || '',
          last_name:  userData.last_name  || '',
          email:      userData.email      || '',
          phone:      userData.phone      || ''
        }));
        setFamilyForm(p => ({ ...p, primary_member_id: userData.id }));
        
        await Promise.all([
          loadDashboard(userData.id),
          loadRaffle(),
          loadBookings(),
          loadEvents(),
          loadSections(),
          loadMembershipCategories(),
          loadMyFeedbacks()
        ]);
        
        if (userData.account_type === 'admin') {
          await loadCategoryStats();
        }
        
        // Load pending approvals if user is primary
        if (userData.membership_type === 'primary') {
          await fetchPendingApprovals();
        }
      } catch(e) {
        showToast('Failed to load dashboard', 'error');
      } finally { setLoading(false); }
    })();
  }, [navigate]);

  useEffect(() => {
    const fn = () => { if (window.innerWidth > 900) setSidebar(false); };
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  const loadDashboard = async (uid) => {
    try {
        const r = await DashboardService.getDashboard(uid);
        console.log('📊 Dashboard API Response:', r);
        if (r.success && r.dashboard) {
            setDashboardData({
                member_info: r.dashboard.member_info || {},
                family_members: r.dashboard.family_members || [],
                upcoming_events: r.dashboard.upcoming_events || [],
                recent_activities: r.dashboard.recent_activities || [],
                stats: r.dashboard.stats || { total_members:0, active_members:0, family_members:0, upcoming_events:0 }
            });
        }
    } catch (error) {
        console.error('Error loading dashboard:', error);
    }
  };

  const loadRaffle = async () => {
    try {
      const [ev, tk, wn] = await Promise.all([
        raffleService.getActiveEvents(),
        raffleService.getMyTickets(),
        raffleService.getMyWinnings()
      ]);
      if (ev.success && ev.events) {
        const now = new Date();
        const up = ev.events.find(e => new Date(e.draw_date) > now);
        setActiveRaffle(up || null);
      }
      if (tk.success) setMyRaffleTickets(tk.tickets || []);
      if (wn.success) setRaffleWinnings(wn.winnings || []);
    } catch {}
  };

  const loadBookings = async () => {
    try {
      const r = await bookingService.getUserBookings();
      if (r.success && r.bookings) {
        setRecentBookings(r.bookings.slice(0,5));
        setPendingBookings(r.bookings.filter(b => b.status === 'pending'));
      }
    } catch {}
  };

  const handleLogout = () => {
    ['nlngra_token','nlngra_user','nlngra_jwt','nlngra_session'].forEach(k => localStorage.removeItem(k));
    navigate('/login');
  };

  const handleRefresh = async () => {
    if (!user) return;
    setLoading(true);
    await Promise.all([loadDashboard(user.id), loadRaffle(), loadBookings(), loadEvents(), loadSections(), loadCategoryStats(), loadMyFeedbacks()]);
    if (user.membership_type === 'primary') {
      await fetchPendingApprovals();
    }
    setLoading(false);
    showToast('Dashboard refreshed');
  };

  const handleAddFamily = async (e) => {
    e.preventDefault();
    
    if (!familyForm.full_name || !familyForm.email) { 
      showToast('Name and email required','error'); 
      return; 
    }
    
    const userStr = localStorage.getItem('nlngra_user');
    const currentUser = userStr ? JSON.parse(userStr) : null;
    
    const nameParts = familyForm.full_name.trim().split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    const requestData = {
      first_name: firstName,
      last_name: lastName,
      email: familyForm.email,
      phone: familyForm.phone || '',
      relationship: familyForm.relationship,
      date_of_birth: familyForm.date_of_birth || null,
      address: familyForm.address || ''
    };
    
    try {
      const r = await DashboardService.addFamilyMember(requestData);
      
      if (r.success) {
        showToast(`Family member added successfully!`, 'success');
        
        setFamilyForm({
          primary_member_id: currentUser?.id || '',
          full_name: '',
          email: '',
          phone: '',
          relationship: 'spouse',
          date_of_birth: '',
          address: ''
        });
        setShowAddFamily(false);
        
        const fr = await DashboardService.getFamilyMembers(currentUser?.id);
        if (fr.success) {
          setDashboardData(p => ({ ...p, family_members: fr.family_members || [] }));
        }
      } else {
        showToast(r.message || r.error || 'Failed to add member', 'error');
      }
    } catch (error) {
      console.error('Add family error:', error);
      showToast(error.message || 'Failed to add member', 'error');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    if (profileForm.new_password && profileForm.new_password !== profileForm.confirm_password) {
      showToast('Passwords do not match','error'); return;
    }
    try {
      const r = await DashboardService.updateProfile(profileForm);
      if (r.success) {
        showToast('Profile updated!');
        const upd = { ...user, ...r.user, full_name:`${r.user?.first_name} ${r.user?.last_name}` };
        setUser(upd);
        localStorage.setItem('nlngra_user', JSON.stringify(upd));
        setShowEditProfile(false);
        setProfileForm(p => ({ ...p, current_password:'', new_password:'', confirm_password:'' }));
      } else showToast(r.error || 'Failed to update','error');
    } catch { showToast('Failed to update profile','error'); }
  };

  const handleDeleteFamily = async (id) => {
    if (!window.confirm('Remove this family member?')) return;
    try {
      const r = await DashboardService.removeFamilyMember(id);
      if (r.success) {
        showToast('Member removed');
        setDashboardData(p => ({ ...p, family_members: p.family_members.filter(m => m.id !== id) }));
      } else showToast(r.error || 'Failed to remove','error');
    } catch { showToast('Failed to remove','error'); }
  };

  const handleEditFamily = async (e) => {
    e.preventDefault();
    try {
      const r = await DashboardService.updateFamilyMember(editFamilyForm);
      if (r.success) {
        showToast('Member updated!');
        setDashboardData(p => ({
          ...p, family_members: p.family_members.map(m =>
            m.id === parseInt(editFamilyForm.family_member_id)
              ? { ...m, ...editFamilyForm } : m
          )
        }));
        setShowEditFamily(false);
      } else showToast(r.error || 'Failed to update','error');
    } catch { showToast('Failed to update','error'); }
  };

  const openEditFamily = (m) => {
    setEditFamilyForm({ family_member_id:m.id, full_name:m.full_name, phone:m.phone||'', relationship:m.relationship||'spouse' });
    setShowEditFamily(true);
  };

  const isPrimary = user?.membership_type === 'primary';
  const isAdmin = user?.account_type === 'admin';
  const userFullName = user?.full_name || `${user?.first_name||''} ${user?.last_name||''}`.trim();
  const isCaptainOrCoach = user?.account_type === 'admin' || user?.role === 'captain' || user?.role === 'coach';

  const navItems = NAV_ITEMS.filter(n => {
    if (n.primaryOnly && !isPrimary) return false;
    if (n.adminOnly && !isAdmin) return false;
    return true;
  });

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    if (imageUrl.startsWith('/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    return `https://nlngraclub.com/${imageUrl}`;
  };

  const indexOfLastEvent = currentEventPage * eventsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - eventsPerPage;
  const currentEvents = upcomingEvents.slice(indexOfFirstEvent, indexOfLastEvent);
  const totalEventPages = Math.ceil(upcomingEvents.length / eventsPerPage);

  const paginateEvents = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalEventPages) {
      setCurrentEventPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const renderEventPagination = () => {
    if (totalEventPages <= 1) return null;
    
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentEventPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalEventPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return (
      <div className="db-pagination">
        <div className="db-pagination-info">
          Showing {indexOfFirstEvent + 1} - {Math.min(indexOfLastEvent, upcomingEvents.length)} of {upcomingEvents.length} events
        </div>
        <div className="db-pagination-controls">
          <button onClick={() => paginateEvents(1)} disabled={currentEventPage === 1} className="db-pagination-btn">
            <i className="fas fa-angle-double-left"></i>
          </button>
          <button onClick={() => paginateEvents(currentEventPage - 1)} disabled={currentEventPage === 1} className="db-pagination-btn">
            <i className="fas fa-angle-left"></i>
          </button>
          
          {startPage > 1 && (
            <>
              <button onClick={() => paginateEvents(1)} className="db-pagination-btn">1</button>
              {startPage > 2 && <span className="db-pagination-ellipsis">...</span>}
            </>
          )}
          
          {pageNumbers.map(number => (
            <button key={number} onClick={() => paginateEvents(number)} className={`db-pagination-btn ${currentEventPage === number ? 'active' : ''}`}>
              {number}
            </button>
          ))}
          
          {endPage < totalEventPages && (
            <>
              {endPage < totalEventPages - 1 && <span className="db-pagination-ellipsis">...</span>}
              <button onClick={() => paginateEvents(totalEventPages)} className="db-pagination-btn">{totalEventPages}</button>
            </>
          )}
          
          <button onClick={() => paginateEvents(currentEventPage + 1)} disabled={currentEventPage === totalEventPages} className="db-pagination-btn">
            <i className="fas fa-angle-right"></i>
          </button>
          <button onClick={() => paginateEvents(totalEventPages)} disabled={currentEventPage === totalEventPages} className="db-pagination-btn">
            <i className="fas fa-angle-double-right"></i>
          </button>
        </div>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysLeftText = (daysLeft) => {
    if (daysLeft === 0) return 'Expires today';
    if (daysLeft === 1) return 'Expires tomorrow';
    return `${daysLeft} days left`;
  };

  const getCategoryLabel = (categoryValue) => {
    const categories = {
      'junior_member': 'Junior Member',
      'family_member': 'Family Member'
    };
    return categories[categoryValue] || categoryValue?.replace('_', ' ') || 'Family Member';
  };

  if (loading) return (
    <div className="db-loading">
      <div className="db-spinner">
        <svg viewBox="0 0 50 50"><circle cx="25" cy="25" r="20" fill="none" strokeWidth="4"/></svg>
      </div>
      <p>Loading dashboard…</p>
    </div>
  );

  if (!user) return null;

  return (
    <div className="db-root">

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            className={`db-toast db-toast--${toast.type}`}
            initial={{ opacity:0, y:-60, scale:0.95 }}
            animate={{ opacity:1, y:0, scale:1 }}
            exit={{ opacity:0, y:-60, scale:0.95 }}
            transition={{ type:'spring', stiffness:400, damping:28 }}
          >
            <span className="db-toast__icon">
              {toast.type==='success'
                ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              }
            </span>
            <span>{toast.msg}</span>
            <button onClick={() => setToast(null)} className="db-toast__close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            className="db-overlay"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setSidebar(false)}
          />
        )}
      </AnimatePresence>

      {/* ─── Sidebar ─── */}
      <motion.aside
        className={`db-sidebar ${sidebarOpen ? 'db-sidebar--open' : ''}`}
        initial={false}
      >
        <div className="db-sidebar__logo">
          <div className="db-sidebar__logo-mark">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M19.071 4.929A10 10 0 1 0 4.929 19.07"/><path d="M19.071 4.929L21 3m-1.929 1.929L15.5 9"/></svg>
          </div>
          <span className="db-sidebar__logo-text">NLNGRA</span>
        </div>

        <div className="db-sidebar__profile">
          <div className="db-avatar db-avatar--lg">
            {userFullName.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="db-sidebar__profile-info">
            <span className="db-sidebar__name">{userFullName}</span>
            <span className="db-sidebar__role">
              {user.account_type === 'admin' ? 'Administrator'
               : getCombinedMembershipDisplay()}
            </span>
            <span className={`db-pill db-pill--${user.status}`}>{user.status}</span>
            {getUserCategoryDisplay()}
          </div>
        </div>

        <nav className="db-nav">
          {navItems.map(item => (
            <button
              key={item.id}
              className={`db-nav__item ${activeTab===item.id ? 'db-nav__item--active':''}`}
              onClick={() => { setActiveTab(item.id); setSidebar(false); }}
            >
              <span className="db-nav__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d={item.icon}/>
                </svg>
              </span>
              <span className="db-nav__label">{item.label}</span>
              {item.id==='bookings' && pendingBookings.length>0 && (
                <span className="db-badge">{pendingBookings.length}</span>
              )}
              {item.id==='raffle' && myRaffleTickets.length>0 && (
                <span className="db-badge">{myRaffleTickets.length}</span>
              )}
              {item.id==='parent-approvals' && pendingApprovals.length>0 && (
                <span className="db-badge db-badge--amber">{pendingApprovals.length}</span>
              )}
              {activeTab===item.id && (
                <motion.div className="db-nav__indicator" layoutId="nav-indicator" transition={{ type:'spring', stiffness:500, damping:35 }}/>
              )}
            </button>
          ))}
        </nav>

        <div className="db-sidebar__footer">
          <button className="db-sidebar__action" onClick={handleRefresh} disabled={loading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            Refresh
          </button>
          <button className="db-sidebar__action db-sidebar__action--logout" onClick={handleLogout}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
            Sign Out
          </button>
        </div>
      </motion.aside>

      {/* ─── Main ─── */}
      <div className="db-main">
        <header className="db-topbar">
          <button className="db-menu-btn" onClick={() => setSidebar(s => !s)} aria-label="Menu">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          </button>
          <div className="db-topbar__title">
            <h1>{navItems.find(n=>n.id===activeTab)?.label || 'Dashboard'}</h1>
            <p>Welcome back, <strong>{userFullName}</strong></p>
          </div>
          <div className="db-topbar__right">
            <div className="db-topbar__meta">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <div>
                <span>Last login</span>
                <strong>{user.last_login ? new Date(user.last_login).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : 'Today'}</strong>
              </div>
            </div>
            <div className="db-avatar db-avatar--sm" onClick={() => setActiveTab('profile')}>
              {userFullName.charAt(0).toUpperCase()}
            </div>
          </div>
        </header>

        <main className="db-content">
          <AnimatePresence mode="wait">
            {activeTab==='overview' && (
              <motion.div key="overview" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <OverviewTab
                  user={user} dashboardData={dashboardData}
                  pendingBookings={pendingBookings.length}
                  raffleTickets={myRaffleTickets.length}
                  raffleWinnings={raffleWinnings.length}
                  activeRaffle={activeRaffle}
                  upcomingEvents={upcomingEvents}
                  eventsLoading={eventsLoading}
                  onEditProfile={()=>setShowEditProfile(true)}
                  onAddFamily={()=>setShowAddFamily(true)}
                  onViewEvents={()=>setActiveTab('events')}
                  onViewBookings={()=>setActiveTab('bookings')}
                  onViewRaffle={()=>setActiveTab('raffle')}
                  onViewEvent={handleViewEvent}
                  getFullImageUrl={getFullImageUrl}
                  isPrimary={isPrimary}
                />
              </motion.div>
            )}
            {activeTab==='profile' && (
              <motion.div key="profile" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <ProfileTab 
                  user={user} 
                  dashboardData={dashboardData} 
                  onEdit={()=>setShowEditProfile(true)}
                  membershipCategories={membershipCategories}
                  getCombinedMembershipDisplay={getCombinedMembershipDisplay}
                  getMembershipTypeLabel={getMembershipTypeLabel}
                  getCategoryLabelOnly={getCategoryLabelOnly}
                  getCategoryPrefixOnly={getCategoryPrefixOnly}
                />
              </motion.div>
            )}
            {activeTab==='parent-approvals' && isPrimary && (
              <motion.div key="parent-approvals" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <ParentApprovalsTab
                  pendingApprovals={pendingApprovals}
                  approvalHistory={approvalHistory}
                  loading={approvalsLoading}
                  processingId={processingApprovalId}
                  onApprove={handleApprove}
                  onDecline={handleDecline}
                  onRefresh={fetchPendingApprovals}
                  formatDate={formatDate}
                  getDaysLeftText={getDaysLeftText}
                  getCategoryLabel={getCategoryLabel}
                />
              </motion.div>
            )}
            {activeTab==='family' && isPrimary && (
              <motion.div key="family" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <FamilyTab
                  members={dashboardData.family_members}
                  onAdd={()=>setShowAddFamily(true)}
                  onEdit={openEditFamily}
                  onRemove={handleDeleteFamily}
                />
              </motion.div>
            )}
            {activeTab==='bookings' && (
              <motion.div key="bookings" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <BookingsTab
                  recent={recentBookings} pending={pendingBookings}
                  onViewAll={()=>navigate('/booking?tab=my-bookings')}
                />
              </motion.div>
            )}
            {activeTab==='raffle' && (
              <motion.div key="raffle" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <RaffleTab
                  activeRaffle={activeRaffle} tickets={myRaffleTickets} winnings={raffleWinnings}
                  onViewAll={()=>navigate('/booking?tab=raffle')}
                />
              </motion.div>
            )}
            {activeTab==='events' && (
              <motion.div key="events" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <EventsTab 
                  events={currentEvents}
                  allEvents={upcomingEvents}
                  loading={eventsLoading}
                  onViewEvent={handleViewEvent}
                  getFullImageUrl={getFullImageUrl}
                  pagination={renderEventPagination()}
                  currentPage={currentEventPage}
                  totalPages={totalEventPages}
                  eventsPerPage={eventsPerPage}
                  totalEvents={upcomingEvents.length}
                />
              </motion.div>
            )}
            {activeTab==='sections' && (
              <motion.div key="sections" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <SectionsTab 
                  sections={mySections}
                  allSections={allSections}
                  loading={sectionsLoading}
                  isCaptainOrCoach={isCaptainOrCoach}
                  onEditSection={(section) => {
                    setEditingSection(section);
                    setSectionFormData({
                      name: section.name || '',
                      category: section.category || '',
                      description: section.description || '',
                      instructor: section.instructor || '',
                      schedule: section.schedule || '',
                      duration: section.duration || '',
                      location: section.location || '',
                      capacity: section.capacity || '',
                      level: section.level || '',
                      age_group: section.age_group || '',
                      image: section.image || ''
                    });
                    setShowSectionModal(true);
                  }}
                />
              </motion.div>
            )}
            {activeTab==='staff-roles' && (
              <motion.div key="staff-roles" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <StaffAssignment user={user} addNotification={showToast} />
              </motion.div>
            )}
            {activeTab==='categories' && isAdmin && (
              <motion.div key="categories" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <CategoriesTab 
                  categories={membershipCategories}
                  categoryStats={categoryStats}
                  onViewCategoryMembers={loadMembersByCategory}
                  onRefreshStats={loadCategoryStats}
                />
              </motion.div>
            )}
            {activeTab==='activities' && (
              <motion.div key="activities" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <ActivitiesTab activities={dashboardData.recent_activities}/>
              </motion.div>
            )}
            {activeTab==='my-feedback' && (
              <motion.div key="my-feedback" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <MyFeedbackTab 
                  feedbacks={myFeedbacks}
                  loading={myFeedbacksLoading}
                  onRefresh={loadMyFeedbacks}
                />
              </motion.div>
            )}
            {activeTab==='feedback' && isAdmin && (
              <motion.div key="feedback" variants={fadeUp} initial="hidden" animate="show" exit="exit">
                <AdminFeedbackPanel />
              </motion.div>
            )}
          </AnimatePresence>
        </main>
      </div>

      {/* ─── Floating Feedback Button (Users only) ─── */}
      {!isAdmin && (
        <button 
          className="feedback-floating-btn"
          onClick={() => setShowFeedbackModal(true)}
          title="Share your feedback"
        >
          <i className="fas fa-comment-dots"></i>
        </button>
      )}

      {/* ─── Feedback Modal ─── */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmitSuccess={() => {
          showToast('Thank you for your feedback!', 'success');
          loadMyFeedbacks();
        }}
        user={user}
      />

      {/* ─── Event View Modal ─── */}
      <AnimatePresence>
        {showEventModal && selectedEvent && (
          <motion.div
            className="db-modal-overlay"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setShowEventModal(false)}
          >
            <motion.div
              className="db-modal db-modal--wide"
              initial={{ scale:0.92, opacity:0, y:30 }}
              animate={{ scale:1, opacity:1, y:0 }}
              exit={{ scale:0.92, opacity:0, y:30 }}
              transition={{ type:'spring', stiffness:380, damping:28 }}
              onClick={e=>e.stopPropagation()}
            >
              <div className="db-modal__header">
                <h3>Event Details</h3>
                <button className="db-modal__close" onClick={() => setShowEventModal(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div className="db-modal__body">
                {selectedEvent.image_url && (
                  <div className="event-full-image-modal">
                    <img src={getFullImageUrl(selectedEvent.image_url)} alt={selectedEvent.title} />
                  </div>
                )}
                <h2 className="event-modal-title">{selectedEvent.title}</h2>
                <div className="event-modal-meta">
                  <span><i className="fas fa-calendar-alt"></i> {new Date(selectedEvent.event_date).toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</span>
                  <span><i className="fas fa-clock"></i> {selectedEvent.event_time ? selectedEvent.event_time.substring(0,5) : 'TBA'}</span>
                  <span><i className="fas fa-map-marker-alt"></i> {selectedEvent.location}</span>
                </div>
                <div className="event-modal-description">
                  <h4>Description</h4>
                  <p>{selectedEvent.description}</p>
                </div>
                {selectedEvent.max_attendees && (
                  <div className="event-modal-capacity">
                    <i className="fas fa-users"></i> Capacity: {selectedEvent.registered_count || 0}/{selectedEvent.max_attendees} spots
                  </div>
                )}
              </div>
              <div className="db-modal__footer">
                <button className="db-btn db-btn--ghost" onClick={() => setShowEventModal(false)}>Close</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Section Edit Modal ─── */}
      <AnimatePresence>
        {showSectionModal && isCaptainOrCoach && (
          <motion.div
            className="db-modal-overlay"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setShowSectionModal(false)}
          >
            <motion.div
              className="db-modal db-modal--wide"
              initial={{ scale:0.92, opacity:0, y:30 }}
              animate={{ scale:1, opacity:1, y:0 }}
              exit={{ scale:0.92, opacity:0, y:30 }}
              transition={{ type:'spring', stiffness:380, damping:28 }}
              onClick={e=>e.stopPropagation()}
            >
              <div className="db-modal__header">
                <h3>{editingSection ? 'Edit Section' : 'Update Section'}</h3>
                <button className="db-modal__close" onClick={() => setShowSectionModal(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <form onSubmit={handleUpdateSection}>
                <div className="db-modal__body">
                  <div className="db-field">
                    <label>Section Name</label>
                    <input type="text" value={sectionFormData.name} onChange={e => setSectionFormData({...sectionFormData, name: e.target.value})} required />
                  </div>
                  <div className="db-field-row">
                    <div className="db-field">
                      <label>Category</label>
                      <input type="text" value={sectionFormData.category} onChange={e => setSectionFormData({...sectionFormData, category: e.target.value})} />
                    </div>
                    <div className="db-field">
                      <label>Instructor/Coach</label>
                      <input type="text" value={sectionFormData.instructor} onChange={e => setSectionFormData({...sectionFormData, instructor: e.target.value})} />
                    </div>
                  </div>
                  <div className="db-field">
                    <label>Description</label>
                    <textarea rows="3" value={sectionFormData.description} onChange={e => setSectionFormData({...sectionFormData, description: e.target.value})} />
                  </div>
                  <div className="db-field-row">
                    <div className="db-field">
                      <label>Schedule</label>
                      <input type="text" value={sectionFormData.schedule} onChange={e => setSectionFormData({...sectionFormData, schedule: e.target.value})} placeholder="e.g., Mon, Wed: 5pm" />
                    </div>
                    <div className="db-field">
                      <label>Duration</label>
                      <input type="text" value={sectionFormData.duration} onChange={e => setSectionFormData({...sectionFormData, duration: e.target.value})} placeholder="e.g., 1 hour" />
                    </div>
                  </div>
                  <div className="db-field-row">
                    <div className="db-field">
                      <label>Location</label>
                      <input type="text" value={sectionFormData.location} onChange={e => setSectionFormData({...sectionFormData, location: e.target.value})} />
                    </div>
                    <div className="db-field">
                      <label>Capacity</label>
                      <input type="number" value={sectionFormData.capacity} onChange={e => setSectionFormData({...sectionFormData, capacity: e.target.value})} />
                    </div>
                  </div>
                  <div className="db-field-row">
                    <div className="db-field">
                      <label>Level</label>
                      <input type="text" value={sectionFormData.level} onChange={e => setSectionFormData({...sectionFormData, level: e.target.value})} placeholder="e.g., Beginner, Intermediate" />
                    </div>
                    <div className="db-field">
                      <label>Age Group</label>
                      <input type="text" value={sectionFormData.age_group} onChange={e => setSectionFormData({...sectionFormData, age_group: e.target.value})} placeholder="e.g., Adults, Kids 8-12" />
                    </div>
                  </div>
                  <div className="db-field">
                    <label>Image URL</label>
                    <input type="text" value={sectionFormData.image} onChange={e => setSectionFormData({...sectionFormData, image: e.target.value})} placeholder="https://..." />
                  </div>
                </div>
                <div className="db-modal__footer">
                  <button type="button" className="db-btn db-btn--ghost" onClick={() => setShowSectionModal(false)}>Cancel</button>
                  <button type="submit" className="db-btn db-btn--primary" disabled={sectionsLoading}>
                    {sectionsLoading ? <i className="fas fa-spinner fa-spin"></i> : 'Save Changes'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Category Members Modal ─── */}
      <AnimatePresence>
        {showCategoryMembers && selectedCategory && (
          <CategoryMembersModal
            category={selectedCategory}
            members={membersByCategory}
            categories={membershipCategories}
            onClose={() => setShowCategoryMembers(false)}
            onUpdateCategory={handleUpdateMemberCategory}
            updating={updatingCategory}
          />
        )}
      </AnimatePresence>

      {/* ─── Modals for Family/Profile ─── */}
      <AnimatePresence>
        {showAddFamily && (
          <Modal title="Add Family Member" onClose={()=>setShowAddFamily(false)}>
            <FamilyForm data={familyForm} setData={setFamilyForm} onSubmit={handleAddFamily} isEdit={false} onClose={()=>setShowAddFamily(false)}/>
          </Modal>
        )}
        {showEditFamily && (
          <Modal title="Edit Family Member" onClose={()=>setShowEditFamily(false)}>
            <FamilyForm data={editFamilyForm} setData={setEditFamilyForm} onSubmit={handleEditFamily} isEdit={true} onClose={()=>setShowEditFamily(false)}/>
          </Modal>
        )}
        {showEditProfile && (
          <Modal title="Edit Profile" onClose={()=>setShowEditProfile(false)} wide>
            <ProfileForm data={profileForm} setData={setProfileForm} onSubmit={handleUpdateProfile} onClose={()=>setShowEditProfile(false)}/>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ════════════════════════════════════════
   PARENT APPROVALS TAB
════════════════════════════════════════ */
const ParentApprovalsTab = ({ pendingApprovals, approvalHistory, loading, processingId, onApprove, onDecline, onRefresh, formatDate, getDaysLeftText, getCategoryLabel }) => {
  const [activeSubTab, setActiveSubTab] = useState('pending');

  if (loading) {
    return (
      <div className="db-loading-small">
        <i className="fas fa-spinner fa-spin"></i> Loading pending approvals...
      </div>
    );
  }

  return (
    <div className="db-section">
      <div className="db-section__head">
        <div>
          <h2>Family Member Approvals</h2>
          <p>Review and manage pending family member registration requests</p>
        </div>
        <button className="db-btn db-btn--ghost" onClick={onRefresh} disabled={loading}>
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
          Refresh
        </button>
      </div>

      {/* Sub Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e5e7eb' }}>
        <button 
          onClick={() => setActiveSubTab('pending')}
          style={{
            padding: '10px 20px',
            background: activeSubTab === 'pending' ? '#00843D' : 'transparent',
            color: activeSubTab === 'pending' ? 'white' : '#4b5563',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
        >
          <i className="fas fa-clock"></i> Pending Requests
          {pendingApprovals.length > 0 && (
            <span style={{
              marginLeft: '8px',
              background: activeSubTab === 'pending' ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
              padding: '2px 8px',
              borderRadius: '20px',
              fontSize: '12px'
            }}>
              {pendingApprovals.length}
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveSubTab('history')}
          style={{
            padding: '10px 20px',
            background: activeSubTab === 'history' ? '#00843D' : 'transparent',
            color: activeSubTab === 'history' ? 'white' : '#4b5563',
            border: 'none',
            borderRadius: '8px 8px 0 0',
            cursor: 'pointer',
            fontWeight: 500,
            transition: 'all 0.2s'
          }}
        >
          <i className="fas fa-history"></i> History
          {approvalHistory.length > 0 && (
            <span style={{
              marginLeft: '8px',
              background: activeSubTab === 'history' ? 'rgba(255,255,255,0.2)' : '#e5e7eb',
              padding: '2px 8px',
              borderRadius: '20px',
              fontSize: '12px'
            }}>
              {approvalHistory.length}
            </span>
          )}
        </button>
      </div>

      {/* Pending Requests */}
      {activeSubTab === 'pending' && (
        <>
          {pendingApprovals.length === 0 ? (
            <div className="db-empty">
              <div className="db-empty__icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <p>No pending approval requests</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {pendingApprovals.map((request) => (
                <div key={request.request_id} className="db-card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <div className="db-avatar db-avatar--lg" style={{ background: '#e8f5e9', color: '#2e7d32' }}>
                        {request.first_name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <h3 style={{ margin: '0 0 4px 0' }}>{request.full_name}</h3>
                        <p style={{ margin: '0 0 2px 0', fontSize: '14px', color: '#6b7280' }}>
                          <i className="fas fa-envelope"></i> {request.email}
                        </p>
                        {request.phone && (
                          <p style={{ margin: '0', fontSize: '14px', color: '#6b7280' }}>
                            <i className="fas fa-phone"></i> {request.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <div style={{ 
                      padding: '8px 12px', 
                      borderRadius: '8px', 
                      background: request.days_left <= 2 ? '#fef3c7' : '#e8f5e9',
                      fontSize: '14px',
                      fontWeight: 500
                    }}>
                      <i className="fas fa-hourglass-half"></i> {getDaysLeftText(request.days_left)}
                    </div>
                  </div>

                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Membership Category</span>
                        <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{getCategoryLabel(request.membership_category)}</p>
                      </div>
                      {request.date_of_birth && (
                        <div>
                          <span style={{ fontSize: '12px', color: '#6b7280' }}>Date of Birth</span>
                          <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{formatDate(request.date_of_birth)}</p>
                        </div>
                      )}
                      <div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Requested On</span>
                        <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{formatDate(request.requested_at)}</p>
                      </div>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6b7280' }}>Expires</span>
                        <p style={{ margin: '4px 0 0', fontWeight: 500 }}>{formatDate(request.token_expires)}</p>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'flex-end' }}>
                    <button 
                      className="db-btn db-btn--primary"
                      onClick={() => onApprove(request.dependent_id, request.approval_token)}
                      disabled={processingId === request.dependent_id}
                    >
                      {processingId === request.dependent_id ? (
                        <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                      ) : (
                        <><i className="fas fa-check"></i> Approve</>
                      )}
                    </button>
                    <button 
                      className="db-btn db-btn--danger"
                      onClick={() => onDecline(request.dependent_id, request.approval_token)}
                      disabled={processingId === request.dependent_id}
                    >
                      {processingId === request.dependent_id ? (
                        <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                      ) : (
                        <><i className="fas fa-times"></i> Decline</>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* History Tab */}
      {activeSubTab === 'history' && (
        <>
          {approvalHistory.length === 0 ? (
            <div className="db-empty">
              <div className="db-empty__icon">
                <i className="fas fa-history"></i>
              </div>
              <p>No approval history found</p>
            </div>
          ) : (
            <div className="db-card">
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <th style={{ textAlign: 'left', padding: '12px' }}>Name</th>
                      <th style={{ textAlign: 'left', padding: '12px' }}>Email</th>
                      <th style={{ textAlign: 'left', padding: '12px' }}>Status</th>
                      <th style={{ textAlign: 'left', padding: '12px' }}>Requested</th>
                      <th style={{ textAlign: 'left', padding: '12px' }}>Resolved</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvalHistory.map((item) => (
                      <tr key={item.request_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '12px' }}>
                          <strong>{item.full_name}</strong>
                          <br />
                          <small style={{ color: '#6b7280' }}>{item.membership_number}</small>
                        </td>
                        <td style={{ padding: '12px' }}>{item.email}</td>
                        <td style={{ padding: '12px' }}>
                          <span className={`db-pill db-pill--${item.status === 'approved' ? 'green' : 'red'}`}>
                            {item.status === 'approved' ? (
                              <><i className="fas fa-check"></i> Approved</>
                            ) : (
                              <><i className="fas fa-times"></i> Declined</>
                            )}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>{formatDate(item.requested_at)}</td>
                        <td style={{ padding: '12px' }}>{formatDate(item.resolved_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   OVERVIEW TAB
════════════════════════════════════════ */
const OverviewTab = ({ user, dashboardData, pendingBookings, raffleTickets, raffleWinnings, activeRaffle, upcomingEvents, eventsLoading, onEditProfile, onAddFamily, onViewEvents, onViewBookings, onViewRaffle, onViewEvent, getFullImageUrl, isPrimary }) => {
  const userFullName = user?.full_name || `${user?.first_name||''} ${user?.last_name||''}`.trim();
  
  const familyMembersCount = dashboardData?.family_members?.length || 0;
  const recentActivities = dashboardData?.recent_activities || [];
  const stats = dashboardData?.stats || {};

  const statsData = [
    { label:'Family Members',    value: familyMembersCount,    sub:'Registered',    color:'green'  },
    { label:'Upcoming Events',   value: upcomingEvents?.length || 0,   sub:'Scheduled',     color:'slate'  },
    { label:'Pending Bookings',  value: pendingBookings || 0,                        sub:'Awaiting approval', color:'amber' },
    { label:'Raffle Tickets',    value: raffleTickets || 0,                          sub:'Active tickets', color:'green'  },
  ];

  return (
    <div className="db-overview">
      <motion.div className="db-stats-row" variants={stagger} initial="hidden" animate="show">
        {statsData.map((s,i) => (
          <motion.div key={i} className={`db-stat db-stat--${s.color}`} variants={slideIn}>
            <div className="db-stat__value">{s.value}</div>
            <div className="db-stat__label">{s.label}</div>
            <div className="db-stat__sub">{s.sub}</div>
          </motion.div>
        ))}
      </motion.div>

      <div className="db-grid-2">
        <div className="db-card">
          <div className="db-card__header">
            <span className="db-card__title">Member Info</span>
            <button className="db-btn db-btn--ghost db-btn--sm" onClick={onEditProfile}>Edit</button>
          </div>
          <div className="db-info-list">
            {[
              ['Full Name', userFullName],
              ['Email',     user?.email || '—'],
              ['Phone',     user?.phone || '—'],
              ['Member No.', user?.membership_number || '—'],
              ['Account',   user?.account_type || '—'],
              ['Status',    <span className={`db-pill db-pill--${user?.status || 'pending'}`}>{user?.status || 'pending'}</span>],
            ].map(([l,v],i)=>(
              <div key={i} className="db-info-row">
                <span className="db-info-row__label">{l}</span>
                <span className="db-info-row__value">{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="db-card">
          <div className="db-card__header">
            <span className="db-card__title">Quick Actions</span>
          </div>
          <div className="db-actions-grid">
            {[
              { icon:'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z', label:'Add Family',  action:onAddFamily,    disabled:!isPrimary },
              { icon:'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', label:'Events',  action:onViewEvents  },
              { icon:'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', label:'Bookings',  action:onViewBookings },
              { icon:'M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z', label:'Raffle', action:onViewRaffle },
            ].map((a,i) => (
              <button key={i} className={`db-qaction ${a.disabled?'db-qaction--disabled':''}`} onClick={a.action} disabled={a.disabled}>
                <span className="db-qaction__icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d={a.icon}/>
                  </svg>
                </span>
                <span>{a.label}</span>
              </button>
            ))}
          </div>

          {activeRaffle && (
            <div className="db-raffle-teaser" onClick={onViewRaffle}>
              <div className="db-raffle-teaser__dot"/>
              <div>
                <strong>{activeRaffle.title}</strong>
                <span>Draw on {new Date(activeRaffle.draw_date).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}</span>
              </div>
              <svg className="db-raffle-teaser__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </div>
          )}
        </div>
      </div>

      <div className="db-grid-2">
        <div className="db-card">
          <div className="db-card__header"><span className="db-card__title">Recent Activity</span></div>
          {recentActivities.length > 0
            ? recentActivities.slice(0,5).map(a => <ActivityRow key={a.id} item={a}/>)
            : <Empty msg="No recent activity"/>}
        </div>
        <div className="db-card">
          <div className="db-card__header">
            <span className="db-card__title">Upcoming Events</span>
            <button className="db-btn db-btn--ghost db-btn--sm" onClick={onViewEvents}>View All</button>
          </div>
          {eventsLoading ? (
            <div className="db-loading-small"><i className="fas fa-spinner fa-spin"></i> Loading events...</div>
          ) : upcomingEvents && upcomingEvents.length > 0 ? (
            upcomingEvents.slice(0,3).map(e => <EventCardCompact key={e.id} event={e} onView={onViewEvent} getFullImageUrl={getFullImageUrl} />)
          ) : (
            <Empty msg="No upcoming events"/>
          )}
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   COMPACT EVENT CARD FOR OVERVIEW
════════════════════════════════════════ */
const EventCardCompact = ({ event, onView, getFullImageUrl }) => {
  const d = new Date(event.event_date);
  return (
    <div className="db-event-compact">
      <div className="db-event-compact__date">
        <span className="db-event-compact__day">{d.getDate()}</span>
        <span className="db-event-compact__month">{d.toLocaleString('default',{month:'short'})}</span>
      </div>
      <div className="db-event-compact__content">
        <h4>{event.title}</h4>
        <div className="db-event-compact__meta">
          <span>📍 {event.location}</span>
          <span>🕐 {event.event_time ? event.event_time.substring(0,5) : 'TBA'}</span>
        </div>
      </div>
      <button className="db-btn db-btn--ghost db-btn--sm" onClick={() => onView(event)}>View</button>
    </div>
  );
};

/* ════════════════════════════════════════
   PROFILE TAB
════════════════════════════════════════ */
const ProfileTab = ({ user, dashboardData, onEdit, membershipCategories, getCombinedMembershipDisplay, getMembershipTypeLabel, getCategoryLabelOnly, getCategoryPrefixOnly }) => {
  const name = user?.full_name || `${user?.first_name||''} ${user?.last_name||''}`.trim();
  
  const recentActivities = dashboardData?.recent_activities || [];
  const familyMembers = dashboardData?.family_members || [];
  const upcomingEvents = dashboardData?.upcoming_events || [];

  const getCategoryDisplay = () => {
    if (!user?.membership_category) return 'Not specified';
    const category = membershipCategories?.find(c => c.value === user.membership_category);
    return category ? category.label : user.membership_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getCategoryPrefix = () => {
    if (!user?.membership_category) return null;
    const category = membershipCategories?.find(c => c.value === user.membership_category);
    return category ? category.prefix : null;
  };

  const getProfileCombinedDisplay = () => {
    const typeLabel = getMembershipTypeLabel ? getMembershipTypeLabel() : (user?.membership_type === 'primary' ? 'Primary Member' : 'Dependent Member');
    const categoryLabel = getCategoryLabelOnly ? getCategoryLabelOnly() : getCategoryDisplay();
    if (categoryLabel && categoryLabel !== 'Not specified') {
      return `${typeLabel} - ${categoryLabel}`;
    }
    return typeLabel;
  };

  return (
    <div className="db-profile-page">
      <div className="db-profile-hero">
        <div className="db-avatar db-avatar--xl">{name.charAt(0).toUpperCase() || 'U'}</div>
        <div>
          <h2>{name}</h2>
          <p>{user?.email || 'No email'}</p>
          <div className="db-profile-hero__badges">
            <span className={`db-pill db-pill--${user?.status || 'pending'}`}>{user?.status || 'pending'}</span>
            <span className="db-pill db-pill--outline">
              {getProfileCombinedDisplay()}
            </span>
            {user?.membership_category && (
              <span className="db-pill db-pill--category">
                {getCategoryPrefix() && <span className="db-category-prefix-small">{getCategoryPrefix()}</span>}
                {getCategoryDisplay()}
              </span>
            )}
          </div>
        </div>
        <button className="db-btn db-btn--primary db-profile-hero__edit" onClick={onEdit}>Edit Profile</button>
      </div>

      <div className="db-grid-3">
        <div className="db-stat db-stat--green">
          <div className="db-stat__value">{recentActivities.length}</div>
          <div className="db-stat__label">Activities</div>
        </div>
        <div className="db-stat db-stat--slate">
          <div className="db-stat__value">{familyMembers.length}</div>
          <div className="db-stat__label">
            {user?.membership_type === 'primary' ? 'Family Members' : 'Family Connection'}
          </div>
        </div>
        <div className="db-stat db-stat--green">
          <div className="db-stat__value">{upcomingEvents.length}</div>
          <div className="db-stat__label">Events</div>
        </div>
      </div>

      <div className="db-grid-2">
        <div className="db-card">
          <div className="db-card__header"><span className="db-card__title">Personal Details</span></div>
          <div className="db-info-list">
            {[
              ['Full Name',  name],
              ['Email',      user?.email || '—'],
              ['Phone',      user?.phone || '—'],
              ['Member No.', user?.membership_number || '—'],
              ['Membership', getProfileCombinedDisplay()],
              ...(user?.relationship_info ? [
                ['Relationship', user.relationship_info.relationship],
                ['Primary Member', user.relationship_info.primary_member_name],
                ['Primary Member Email', user.relationship_info.primary_member_email],
                ['Connected Since', new Date(user.relationship_info.connected_since).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'})]
              ] : [])
            ].map(([l,v],i) => <div key={i} className="db-info-row"><span className="db-info-row__label">{l}</span><span className="db-info-row__value">{v}</span></div>)}
          </div>
        </div>
        <div className="db-card">
          <div className="db-card__header"><span className="db-card__title">Account Details</span></div>
          <div className="db-info-list">
            {[
              ['Account Type', user?.account_type === 'admin' ? 'Administrator' : (user?.membership_type === 'primary' ? 'Primary Member' : 'Family Member')],
              ['Status',       <span className={`db-pill db-pill--${user?.status || 'pending'}`}>{user?.status || 'pending'}</span>],
              ['Member Since', user?.created_at ? new Date(user.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : '—'],
              ['Last Login',   user?.last_login ? new Date(user.last_login).toLocaleString('en-GB',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : 'Never'],
            ].map(([l,v],i) => <div key={i} className="db-info-row"><span className="db-info-row__label">{l}</span><span className="db-info-row__value">{v}</span></div>)}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   FAMILY TAB
════════════════════════════════════════ */
const FamilyTab = ({ members, onAdd, onEdit, onRemove }) => (
  <div className="db-section">
    <div className="db-section__head">
      <div><h2>Family Members</h2><p>{members.length} member{members.length!==1?'s':''} registered</p></div>
      <button className="db-btn db-btn--primary" onClick={onAdd}>+ Add Member</button>
    </div>
    {members.length > 0 ? (
      <motion.div className="db-family-grid" variants={stagger} initial="hidden" animate="show">
        {members.map(m => (
          <motion.div key={m.id} className="db-family-card" variants={slideIn}>
            <div className="db-family-card__top">
              <div className="db-avatar db-avatar--md">{m.full_name?.charAt(0)?.toUpperCase() || m.first_name?.charAt(0)?.toUpperCase() || 'F'}</div>
              <div>
                <strong>{m.full_name || `${m.first_name} ${m.last_name}`}</strong>
                <span>{m.email}</span>
                <div className="db-family-card__pills">
                  <span className={`db-pill db-pill--${m.status || 'active'}`}>{m.status || 'active'}</span>
                  <span className="db-pill db-pill--outline">{m.relationship}</span>
                </div>
              </div>
            </div>
            <div className="db-family-card__meta">
              <span>{m.phone || 'No phone'}</span>
              <span>Added {new Date(m.added_at || m.created_at || m.connected_since).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}</span>
            </div>
            <div className="db-family-card__actions">
              <button className="db-btn db-btn--ghost db-btn--sm" onClick={()=>onEdit(m)}>Edit</button>
              <button className="db-btn db-btn--danger db-btn--sm" onClick={()=>onRemove(m.id)}>Remove</button>
            </div>
          </motion.div>
        ))}
      </motion.div>
    ) : <Empty msg="No family members added yet" />}
  </div>
);

/* ════════════════════════════════════════
   BOOKINGS TAB
════════════════════════════════════════ */
const BookingsTab = ({ recent, pending, onViewAll }) => {
  const statusClass = s => ({ approved:'green', pending:'amber', rejected:'red', cancelled:'slate' }[s] || 'slate');
  return (
    <div className="db-section">
      <div className="db-section__head">
        <div><h2>My Bookings</h2><p>{recent.length} total booking{recent.length!==1?'s':''}</p></div>
        <button className="db-btn db-btn--primary" onClick={onViewAll}>Browse Facilities</button>
      </div>

      {pending.length > 0 && (
        <div className="db-card db-card--highlight">
          <div className="db-card__header"><span className="db-card__title">Pending Approval</span><span className="db-badge">{pending.length}</span></div>
          {pending.slice(0,3).map(b => (
            <div key={b.id} className="db-booking-row">
              <div className="db-booking-row__info">
                <strong>{b.facility_name||b.location||'Facility'}</strong>
                <span>{b.booking_date ? new Date(b.booking_date).toLocaleDateString('en-GB') : 'N/A'}</span>
              </div>
              <span className={`db-pill db-pill--${statusClass(b.status)}`}>{b.status}</span>
            </div>
          ))}
        </div>
      )}

      <div className="db-card">
        <div className="db-card__header"><span className="db-card__title">Recent Bookings</span></div>
        {recent.length > 0 ? recent.map(b => (
          <div key={b.id} className="db-booking-row">
            <div className="db-booking-row__info">
              <strong>{b.facility_name||b.location||'Facility'}</strong>
              <span>{b.booking_date ? new Date(b.booking_date).toLocaleDateString('en-GB') : 'N/A'}</span>
            </div>
            <span className={`db-pill db-pill--${statusClass(b.status)}`}>{b.status}</span>
          </div>
        )) : <Empty msg="No bookings yet"/>}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   RAFFLE TAB
════════════════════════════════════════ */
const RaffleTab = ({ activeRaffle, tickets, winnings, onViewAll }) => {
  const [cd, setCd] = useState({ d:0, h:0, m:0, s:0 });

  useEffect(() => {
    if (!activeRaffle?.draw_date) return;
    const tick = () => {
      const diff = new Date(activeRaffle.draw_date) - new Date();
      if (diff > 0) setCd({
        d: Math.floor(diff/86400000),
        h: Math.floor((diff%86400000)/3600000),
        m: Math.floor((diff%3600000)/60000),
        s: Math.floor((diff%60000)/1000),
      });
    };
    tick(); const iv = setInterval(tick,1000); return ()=>clearInterval(iv);
  }, [activeRaffle]);

  return (
    <div className="db-section">
      <div className="db-section__head">
        <div><h2>Raffle Draw</h2></div>
        <button className="db-btn db-btn--primary" onClick={onViewAll}>Buy Tickets</button>
      </div>

      {activeRaffle ? (
        <div className="db-raffle-hero">
          <div className="db-raffle-hero__label">Active Draw</div>
          <h3>{activeRaffle.title}</h3>
          <div className="db-countdown">
            {[['Days',cd.d],['Hours',cd.h],['Mins',cd.m],['Secs',cd.s]].map(([l,v]) => (
              <div key={l} className="db-countdown__unit">
                <AnimatePresence mode="popLayout">
                  <motion.span key={v} className="db-countdown__num"
                    initial={{y:-20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}}
                    transition={{duration:0.25}}>
                    {String(v).padStart(2,'0')}
                  </motion.span>
                </AnimatePresence>
                <span className="db-countdown__label">{l}</span>
              </div>
            ))}
          </div>
          <div className="db-raffle-hero__meta">
            <span>₦{Number(activeRaffle.ticket_price).toLocaleString()} per ticket</span>
            <span>{activeRaffle.total_prizes||0} prizes to win</span>
          </div>
        </div>
      ) : <div className="db-card"><Empty msg="No active raffle events"/></div>}

      {tickets.length > 0 && (
        <div className="db-card">
          <div className="db-card__header"><span className="db-card__title">My Tickets</span><span className="db-badge">{tickets.length}</span></div>
          <div className="db-ticket-list">
            {tickets.slice(0,5).map(t => (
              <div key={t.id} className="db-ticket-row">
                <span className="db-ticket-row__num">{t.ticket_number}</span>
                <span className={`db-pill db-pill--${t.payment_status==='paid'?'green':'amber'}`}>{t.payment_status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {winnings.length > 0 && (
        <div className="db-card">
          <div className="db-card__header"><span className="db-card__title">My Winnings</span><span className="db-badge db-badge--gold">{winnings.length}</span></div>
          {winnings.slice(0,3).map(w => (
            <div key={w.id} className="db-winning-row">
              <span className="db-winning-row__icon">🏆</span>
              <span className="db-winning-row__name">{w.prize_name}</span>
              <span className={`db-pill db-pill--${w.claimed?'green':'amber'}`}>{w.claimed?'Claimed':'Unclaimed'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   EVENTS TAB
════════════════════════════════════════ */
const EventsTab = ({ events, loading, onViewEvent, getFullImageUrl, pagination, totalEvents }) => {
  const formatTime = (timeString) => {
    if (!timeString) return 'TBA';
    return timeString.substring(0, 5);
  };

  return (
    <div className="db-section">
      <div className="db-section__head">
        <div>
          <h2>Upcoming Events</h2>
          <p>{totalEvents} event{totalEvents !== 1 ? 's' : ''} scheduled</p>
        </div>
      </div>
      
      {loading ? (
        <div className="db-loading-small"><i className="fas fa-spinner fa-spin"></i> Loading events...</div>
      ) : events.length > 0 ? (
        <>
          <div className="db-events-grid">
            {events.map(event => (
              <div key={event.id} className="db-event-card-full">
                {event.image_url && (
                  <div className="db-event-card-full__image">
                    <img src={getFullImageUrl(event.image_url)} alt={event.title} />
                  </div>
                )}
                <div className="db-event-card-full__content">
                  <div className="db-event-card-full__date">
                    <span className="db-event-card-full__day">{new Date(event.event_date).getDate()}</span>
                    <span className="db-event-card-full__month">{new Date(event.event_date).toLocaleString('default',{month:'short'}).toUpperCase()}</span>
                  </div>
                  <h3>{event.title}</h3>
                  <p>{event.description}</p>
                  <div className="db-event-card-full__meta">
                    <span><i className="fas fa-map-marker-alt"></i> {event.location}</span>
                    <span><i className="fas fa-clock"></i> {formatTime(event.event_time)}</span>
                    <span><i className="fas fa-users"></i> {event.registered_count || 0}/{event.max_attendees} spots</span>
                  </div>
                  <button 
                    className="db-btn db-btn--ghost" 
                    onClick={() => onViewEvent(event)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
          {pagination}
        </>
      ) : (
        <Empty msg="No upcoming events scheduled"/>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   SECTIONS TAB
════════════════════════════════════════ */
const SectionsTab = ({ sections, allSections, loading, isCaptainOrCoach, onEditSection }) => {
  const getSectionName = (sectionId) => {
    const section = allSections.find(s => s.id === parseInt(sectionId));
    return section ? section.name : `Section #${sectionId}`;
  };

  const getSectionDetail = (sectionId, field) => {
    const section = allSections.find(s => s.id === parseInt(sectionId));
    return section ? section[field] : '';
  };

  return (
    <div className="db-section">
      <div className="db-section__head">
        <div>
          <h2>My Sections</h2>
          <p>{sections.length} section{sections.length !== 1 ? 's' : ''} you're a member of</p>
        </div>
      </div>
      
      {loading ? (
        <div className="db-loading-small"><i className="fas fa-spinner fa-spin"></i> Loading sections...</div>
      ) : sections.length > 0 ? (
        <div className="db-sections-grid">
          {sections.map(section => {
            const sectionName = section.section_name || getSectionName(section.section_id);
            const sectionCategory = section.section_category || getSectionDetail(section.section_id, 'category');
            const sectionDescription = section.section_description || getSectionDetail(section.section_id, 'description');
            const sectionInstructor = section.section_instructor || getSectionDetail(section.section_id, 'instructor');
            const sectionSchedule = section.section_schedule || getSectionDetail(section.section_id, 'schedule');
            const sectionLocation = section.section_location || getSectionDetail(section.section_id, 'location');
            const sectionImage = section.section_image || getSectionDetail(section.section_id, 'image');
            
            return (
              <div key={section.id} className="db-section-card">
                {sectionImage && (
                  <div className="db-section-card__image">
                    <img src={sectionImage} alt={sectionName} />
                  </div>
                )}
                <div className="db-section-card__content">
                  <div className="db-section-card__header">
                    <h3>{sectionName}</h3>
                    <span className={`db-pill db-pill--${section.status}`}>{section.status}</span>
                  </div>
                  {sectionCategory && <p className="db-section-category">📁 {sectionCategory}</p>}
                  <p className="db-section-description">{sectionDescription || 'No description available'}</p>
                  <div className="db-section-card__meta">
                    {sectionInstructor && <span><i className="fas fa-user"></i> {sectionInstructor}</span>}
                    {sectionSchedule && <span><i className="fas fa-clock"></i> {sectionSchedule}</span>}
                    {sectionLocation && <span><i className="fas fa-map-marker-alt"></i> {sectionLocation}</span>}
                  </div>
                  <div className="db-section-card__request-info">
                    <small>Requested: {new Date(section.created_at).toLocaleDateString()}</small>
                    {section.status === 'pending' && (
                      <span className="db-pill db-pill--amber">Waiting for approval</span>
                    )}
                    {section.status === 'approved' && (
                      <span className="db-pill db-pill--green">Active Member</span>
                    )}
                  </div>
                  {isCaptainOrCoach && (
                    <button className="db-btn db-btn--ghost db-btn--sm" onClick={() => {
                      const sectionDetail = allSections.find(s => s.id === parseInt(section.section_id));
                      if (sectionDetail) onEditSection(sectionDetail);
                    }}>
                      <i className="fas fa-edit"></i> Update Section
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <Empty msg="You haven't joined any sections yet. Browse sections to join!"/>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   CATEGORIES TAB (Admin Only)
════════════════════════════════════════ */
const CategoriesTab = ({ categories, categoryStats, onViewCategoryMembers, onRefreshStats }) => {
  const getCategoryColor = (categoryValue) => {
    const colors = {
      'full_member': '#2e7d32',
      'junior_member': '#1565c0',
      'family_member': '#6a1b9a',
      'honorary_member': '#c62828',
      'associate_member': '#e65100',
      'non_bonny_staff': '#00838f'
    };
    return colors[categoryValue] || '#757575';
  };

  return (
    <div className="db-section">
      <div className="db-section__head">
        <div>
          <h2>Membership Categories</h2>
          <p>Manage and view members by category</p>
        </div>
        <button className="db-btn db-btn--ghost" onClick={onRefreshStats}>
          <i className="fas fa-sync-alt"></i> Refresh Stats
        </button>
      </div>

      <div className="db-categories-grid">
        {categories.map(category => {
          const stats = categoryStats?.find(s => s.category === category.value) || { count: 0 };
          return (
            <div 
              key={category.value} 
              className="db-category-card"
              style={{ borderTopColor: getCategoryColor(category.value) }}
              onClick={() => onViewCategoryMembers(category.value)}
            >
              <div className="db-category-card__header">
                <span className="db-category-prefix-large" style={{ backgroundColor: getCategoryColor(category.value) }}>
                  {category.prefix}
                </span>
                <span className="db-category-count">{stats.count}</span>
              </div>
              <div className="db-category-card__body">
                <h3>{category.label}</h3>
                <p className="db-category-description">{category.description}</p>
                <div className="db-category-note">
                  <i className="fas fa-lightbulb"></i>
                  <span>{category.note}</span>
                </div>
              </div>
              <div className="db-category-card__footer">
                <button className="db-btn db-btn--sm db-btn--ghost">
                  View {stats.count} Members →
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {categoryStats && (
        <div className="db-category-summary">
          <h3>Summary</h3>
          <div className="db-summary-stats">
            <div className="db-summary-item">
              <span className="db-summary-label">Total Members:</span>
              <span className="db-summary-value">
                {categoryStats.reduce((sum, s) => sum + s.count, 0)}
              </span>
            </div>
            <div className="db-summary-item">
              <span className="db-summary-label">Categories:</span>
              <span className="db-summary-value">{categories.length}</span>
            </div>
            <div className="db-summary-item">
              <span className="db-summary-label">Most Popular:</span>
              <span className="db-summary-value">
                {categoryStats.sort((a, b) => b.count - a.count)[0]?.label || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════
   CATEGORY MEMBERS MODAL
════════════════════════════════════════ */
const CategoryMembersModal = ({ category, members, categories, onClose, onUpdateCategory, updating }) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  
  const categoryInfo = categories.find(c => c.value === category);
  
  const handleUpdateCategory = async () => {
    if (!selectedMember || !newCategory) return;
    await onUpdateCategory(selectedMember.id, newCategory);
    setSelectedMember(null);
    setNewCategory('');
  };

  return (
    <motion.div
      className="db-modal-overlay"
      initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
      onClick={onClose}
    >
      <motion.div
        className="db-modal db-modal--wide"
        initial={{ scale:0.92, opacity:0, y:30 }}
        animate={{ scale:1, opacity:1, y:0 }}
        exit={{ scale:0.92, opacity:0, y:30 }}
        transition={{ type:'spring', stiffness:380, damping:28 }}
        onClick={e=>e.stopPropagation()}
      >
        <div className="db-modal__header">
          <h3>{categoryInfo?.label} Members ({members.length})</h3>
          <button className="db-modal__close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="db-modal__body">
          {members.length > 0 ? (
            <div className="db-members-table-wrapper">
              <table className="db-members-table">
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Name</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Membership Number</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '12px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(member => (
                    <tr key={member.id}>
                      <td style={{ padding: '12px' }}>
                        <strong>{member.first_name} {member.last_name}</strong>
                        <br />
                        <small style={{ color: '#6b7280' }}>{member.membership_number}</small>
                      </td>
                      <td style={{ padding: '12px' }}>{member.email}</td>
                      <td style={{ padding: '12px' }}>{member.membership_number}</td>
                      <td style={{ padding: '12px' }}>
                        <span className={`db-pill db-pill--${member.status}`}>
                          {member.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button 
                          className="db-btn db-btn--sm db-btn--ghost"
                          onClick={() => {
                            setSelectedMember(member);
                            setNewCategory(member.membership_category || category);
                          }}
                        >
                          Change Category
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="db-empty">
              <div className="db-empty__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
                </svg>
              </div>
              <p>No members found in this category</p>
            </div>
          )}
        </div>
        <div className="db-modal__footer">
          <button className="db-btn db-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </motion.div>

      {/* Change Category Sub-modal */}
      <AnimatePresence>
        {selectedMember && (
          <motion.div
            className="db-modal-sub-overlay"
            initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
            onClick={() => setSelectedMember(null)}
          >
            <motion.div
              className="db-modal-sub"
              initial={{ scale:0.92, opacity:0, y:30 }}
              animate={{ scale:1, opacity:1, y:0 }}
              exit={{ scale:0.92, opacity:0, y:30 }}
              onClick={e=>e.stopPropagation()}
            >
              <div className="db-modal__header">
                <h4>Change Membership Category</h4>
                <button className="db-modal__close" onClick={() => setSelectedMember(null)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
              <div className="db-modal__body">
                <div className="db-member-info">
                  <p><strong>Member:</strong> {selectedMember.first_name} {selectedMember.last_name}</p>
                  <p><strong>Email:</strong> {selectedMember.email}</p>
                  <p><strong>Membership #:</strong> {selectedMember.membership_number}</p>
                  <p><strong>Current Category:</strong> {
                    categories.find(c => c.value === (selectedMember.membership_category || category))?.label || 'Not set'
                  }</p>
                </div>
                <div className="db-field">
                  <label>New Category</label>
                  <select 
                    value={newCategory} 
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="db-select"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label} ({cat.prefix})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="db-info-card">
                  <i className="fas fa-info-circle"></i>
                  <div>
                    <p><strong>Note:</strong> Changing a member's category will affect their access permissions and membership benefits.</p>
                  </div>
                </div>
              </div>
              <div className="db-modal__footer">
                <button className="db-btn db-btn--ghost" onClick={() => setSelectedMember(null)}>
                  Cancel
                </button>
                <button 
                  className="db-btn db-btn--primary" 
                  onClick={handleUpdateCategory}
                  disabled={updating || !newCategory}
                >
                  {updating ? 'Updating...' : 'Update Category'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ════════════════════════════════════════
   ACTIVITIES TAB
════════════════════════════════════════ */
const ActivitiesTab = ({ activities }) => (
  <div className="db-section">
    <div className="db-section__head"><div><h2>Activity History</h2></div></div>
    <div className="db-card">
      {activities.length > 0
        ? activities.map(a => <ActivityRow key={a.id} item={a} detailed/>)
        : <Empty msg="No activity recorded"/>}
    </div>
  </div>
);

/* ════════════════════════════════════════
   MY FEEDBACK TAB (Users) - COMPLETE - SHOWS ALL USER FEEDBACKS
════════════════════════════════════════ */
const MyFeedbackTab = ({ feedbacks, loading, onRefresh }) => {
  const [expandedFeedback, setExpandedFeedback] = useState(null);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'status-pending', icon: 'fa-clock', text: 'Pending Review', bg: '#fef3c7', color: '#d97706' },
      reviewed: { class: 'status-reviewed', icon: 'fa-eye', text: 'Reviewed', bg: '#e0f2fe', color: '#0284c7' },
      responded: { class: 'status-responded', icon: 'fa-reply', text: 'Responded', bg: '#e8f5e9', color: '#27a85a' },
      resolved: { class: 'status-resolved', icon: 'fa-check-circle', text: 'Resolved', bg: '#d1fae5', color: '#059669' }
    };
    return statusConfig[status] || statusConfig.pending;
  };

  const getRatingStars = (rating) => {
    return (
      <div className="my-feedback-stars">
        {[1, 2, 3, 4, 5].map(star => (
          <i key={star} className={`fas fa-star ${star <= rating ? 'active' : 'inactive'}`}></i>
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      general: 'fa-comment',
      facilities: 'fa-building',
      events: 'fa-calendar-alt',
      booking: 'fa-calendar-check',
      staff: 'fa-user-tie',
      suggestion: 'fa-lightbulb',
      complaint: 'fa-exclamation-triangle'
    };
    return icons[category] || 'fa-comment';
  };

  const getCategoryLabel = (category) => {
    const labels = {
      general: 'General Feedback',
      facilities: 'Facilities',
      events: 'Events',
      booking: 'Booking Experience',
      staff: 'Staff Service',
      suggestion: 'Suggestion',
      complaint: 'Complaint'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="my-feedback-loading">
        <i className="fas fa-spinner fa-spin"></i>
        <p>Loading your feedback history...</p>
      </div>
    );
  }

  if (!feedbacks || feedbacks.length === 0) {
    return (
      <div className="my-feedback-empty">
        <div className="empty-state-icon">
          <i className="fas fa-comment-dots"></i>
        </div>
        <h3>No feedback submitted yet</h3>
        <p>Your feedback helps us improve the club experience. Click the feedback button to share your thoughts!</p>
      </div>
    );
  }

  return (
    <div className="my-feedback-section">
      <div className="db-section__head">
        <div>
          <h2>My Feedback History <i className="fas fa-list-ul"></i></h2>
          <p>You have submitted {feedbacks.length} feedback{feedbacks.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="db-btn db-btn--ghost" onClick={onRefresh} disabled={loading}>
          <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
          Refresh
        </button>
      </div>

      <div className="my-feedback-list">
        {feedbacks.map((feedback) => {
          const status = getStatusBadge(feedback.status);
          const isExpanded = expandedFeedback === feedback.id;
          
          return (
            <div key={feedback.id} className={`my-feedback-card ${feedback.status}`}>
              <div className="feedback-card-header">
                <div className="feedback-header-left">
                  <div className="feedback-category-badge">
                    <i className={`fas ${getCategoryIcon(feedback.category)}`}></i>
                    <span>{getCategoryLabel(feedback.category)}</span>
                  </div>
                  {getRatingStars(feedback.rating)}
                </div>
                <div className="feedback-status" style={{ background: status.bg, color: status.color }}>
                  <i className={`fas ${status.icon}`}></i>
                  <span>{status.text}</span>
                </div>
              </div>

              <div className="feedback-card-body">
                <p className="feedback-comment">{feedback.comment}</p>
                <div className="feedback-meta">
                  <span>
                    <i className="fas fa-calendar-alt"></i>
                    Submitted: {formatDate(feedback.submitted_at)}
                  </span>
                  {feedback.responded_at && (
                    <span>
                      <i className="fas fa-reply-all"></i>
                      Responded: {formatDate(feedback.responded_at)}
                    </span>
                  )}
                </div>
              </div>

              {feedback.admin_response && (
                <div className={`feedback-admin-response ${isExpanded ? 'expanded' : 'collapsed'}`}>
                  <div className="response-header">
                    <i className="fas fa-reply-all"></i>
                    <strong>Admin Response:</strong>
                    <button 
                      className="toggle-response"
                      onClick={() => setExpandedFeedback(isExpanded ? null : feedback.id)}
                    >
                      <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                      {isExpanded ? 'Show less' : 'Show response'}
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="response-content">
                      <p>{feedback.admin_response}</p>
                      {feedback.responded_at && (
                        <div className="response-date">
                          <i className="fas fa-clock"></i>
                          {formatDate(feedback.responded_at)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {feedback.status === 'pending' && (
                <div className="feedback-pending-note">
                  <i className="fas fa-hourglass-half"></i>
                  <span>Your feedback is awaiting review. We'll respond as soon as possible.</span>
                </div>
              )}

              {feedback.status === 'resolved' && (
                <div className="feedback-resolved-note">
                  <i className="fas fa-check-circle"></i>
                  <span>This feedback has been marked as resolved. Thank you for your input!</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   SHARED SMALL COMPONENTS
════════════════════════════════════════ */
const activityIcon = (type) => ({
  login:           'M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1',
  profile_updated: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  family_added:    'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
  family_removed:  'M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6',
  password_changed:'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
})[type] || 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z';

const ActivityRow = ({ item, detailed }) => (
  <div className="db-activity-row">
    <div className="db-activity-row__icon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
        <path d={activityIcon(item.activity_type)}/>
      </svg>
    </div>
    <div className="db-activity-row__body">
      <span className="db-activity-row__desc">{item.description}</span>
      <span className="db-activity-row__time">{new Date(item.created_at).toLocaleString('en-GB',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'})}</span>
      {detailed && item.ip_address && <span className="db-activity-row__ip">IP: {item.ip_address}</span>}
    </div>
  </div>
);

const Empty = ({ msg }) => (
  <div className="db-empty">
    <div className="db-empty__icon">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
      </svg>
    </div>
    <p>{msg}</p>
  </div>
);

const Modal = ({ title, onClose, children, wide }) => (
  <motion.div
    className="db-modal-overlay"
    initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}
    onClick={onClose}
  >
    <motion.div
      className={`db-modal ${wide?'db-modal--wide':''}`}
      initial={{ scale:0.92, opacity:0, y:30 }}
      animate={{ scale:1, opacity:1, y:0 }}
      exit={{ scale:0.92, opacity:0, y:30 }}
      transition={{ type:'spring', stiffness:380, damping:28 }}
      onClick={e=>e.stopPropagation()}
    >
      <div className="db-modal__header">
        <h3>{title}</h3>
        <button className="db-modal__close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>
      {children}
    </motion.div>
  </motion.div>
);

const FamilyForm = ({ data, setData, onSubmit, isEdit, onClose }) => (
  <form onSubmit={onSubmit}>
    <div className="db-modal__body">
      <div className="db-field">
        <label>Full Name <span className="db-req">*</span></label>
        <input type="text" value={data.full_name} onChange={e=>setData({...data,full_name:e.target.value})} placeholder="Enter full name" required/>
      </div>
      {!isEdit && (
        <div className="db-field">
          <label>Email Address <span className="db-req">*</span></label>
          <input type="email" value={data.email} onChange={e=>setData({...data,email:e.target.value})} placeholder="Enter email" required/>
          <p className="db-hint">Will be used for login</p>
        </div>
      )}
      <div className="db-field-row">
        <div className="db-field">
          <label>Phone Number</label>
          <input type="tel" value={data.phone} onChange={e=>setData({...data,phone:e.target.value})} placeholder="Phone"/>
        </div>
        <div className="db-field">
          <label>Relationship <span className="db-req">*</span></label>
          <select value={data.relationship} onChange={e=>setData({...data,relationship:e.target.value})} required>
            {['spouse','child','parent','sibling','other'].map(r=><option key={r} value={r}>{r.charAt(0).toUpperCase()+r.slice(1)}</option>)}
          </select>
        </div>
      </div>
      {!isEdit && <>
        <div className="db-field">
          <label>Date of Birth</label>
          <input type="date" value={data.date_of_birth} onChange={e=>setData({...data,date_of_birth:e.target.value})}/>
        </div>
        <div className="db-field">
          <label>Address</label>
          <textarea value={data.address} onChange={e=>setData({...data,address:e.target.value})} placeholder="Enter address" rows={2}/>
        </div>
        <div className="db-info-banner">A new account will be created with auto-generated credentials.</div>
      </>}
    </div>
    <div className="db-modal__footer">
      <button type="button" className="db-btn db-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="submit" className="db-btn db-btn--primary">{isEdit ? 'Save Changes' : 'Add Member'}</button>
    </div>
  </form>
);

const ProfileForm = ({ data, setData, onSubmit, onClose }) => (
  <form onSubmit={onSubmit}>
    <div className="db-modal__body">
      <div className="db-form-section">
        <h4>Personal Information</h4>
        <div className="db-field-row">
          <div className="db-field">
            <label>First Name <span className="db-req">*</span></label>
            <input type="text" value={data.first_name} onChange={e=>setData({...data,first_name:e.target.value})} required/>
          </div>
          <div className="db-field">
            <label>Last Name <span className="db-req">*</span></label>
            <input type="text" value={data.last_name} onChange={e=>setData({...data,last_name:e.target.value})} required/>
          </div>
        </div>
        <div className="db-field">
          <label>Email</label>
          <input type="email" value={data.email} disabled className="db-input--disabled"/>
          <p className="db-hint">Email cannot be changed</p>
        </div>
        <div className="db-field">
          <label>Phone Number</label>
          <input type="tel" value={data.phone} onChange={e=>setData({...data,phone:e.target.value})} placeholder="Phone"/>
        </div>
      </div>
      <div className="db-form-section">
        <h4>Change Password</h4>
        <div className="db-field">
          <label>Current Password</label>
          <input type="password" value={data.current_password} onChange={e=>setData({...data,current_password:e.target.value})} placeholder="Current password"/>
        </div>
        <div className="db-field-row">
          <div className="db-field">
            <label>New Password</label>
            <input type="password" value={data.new_password} onChange={e=>setData({...data,new_password:e.target.value})} placeholder="New password"/>
          </div>
          <div className="db-field">
            <label>Confirm Password</label>
            <input type="password" value={data.confirm_password} onChange={e=>setData({...data,confirm_password:e.target.value})} placeholder="Confirm"/>
          </div>
        </div>
        <p className="db-hint">Leave blank to keep current password</p>
      </div>
    </div>
    <div className="db-modal__footer">
      <button type="button" className="db-btn db-btn--ghost" onClick={onClose}>Cancel</button>
      <button type="submit" className="db-btn db-btn--primary">Save Changes</button>
    </div>
  </form>
);

export default DashboardPage;